import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild } from '@angular/core';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { PaymentService } from '../../services/payment.service';
import { CompanyService } from '../../services/company.service';

import { OpenNotesPaymentInfoListItem, OpenNotesPaymentInfoListGroupFooter, PendingPayment } from 'src/app/models/payment';
import { CompanyInformation } from '../../models/company';

export enum PaymentSelectType {
    Note,
    PayGroup
}

export interface PaymentSelected {
    paymentSelectType: PaymentSelectType;
    selectedId: any;
    selectedData: OpenNotesPaymentInfoListItem[];
}

@Component({
    selector: 'app-payment-note-list',
    templateUrl: './payment-note-list.component.html',
    styleUrls: ['./payment-note-list.component.scss']
})
export class PaymentNoteListComponent implements OnInit, AfterViewInit {
    private _participantId = 0;
    private _noteId = 0;
    private _payGroup = 0;

    @Input()
    set participantId(participantId: number) {
        this._participantId = participantId || 0;
        this.getData();
    }
    get participantId(): number {
        return this._participantId;
    }

    @Input() displayTitle = false;

    @Input() displayNotices = false;

    @Output() selected = new EventEmitter<PaymentSelected>();

    // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
    // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
    private _hideElementDisplayStyle = 'none';
    private _showElementDisplayStyle = 'inline';
    private _showElementDisplayStyleIE = 'inline-block';
    private companyCode: string = null;                 // 10/22/2021

    private _handleError: HandleError;
    private _logging = environment.logging;

    isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

    showSpinner = false;

    openNotesPaymentInfoList: (OpenNotesPaymentInfoListItem)[] = [];
    openNotesPaymentInfoListWithFooters: (OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter)[] = [];

    // DataTable
    tableDisplayedColumns = [
        'noteId',
        'nextPaymentDueDate',
        'noteDate',
        'noteStatus',
        'pastDueAmount',
        'lateFeeDue',
        'regularPaymentAmount',
        'totalAmountDue',
        'payoffAmount',
        'autoPayFlag',
        'options'
    ];
    tableDataSource = new MatTableDataSource<OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter>(
        this.openNotesPaymentInfoListWithFooters
    );
    tableDisplayStyle: string = this._hideElementDisplayStyle;
    tableDisplayStyleIE: string = this._hideElementDisplayStyle;
    openNotesPaymentInfoListFound = false;

    companyInformation: CompanyInformation;
    pendingPayment: PendingPayment;

    filteredNotesList = [...this.openNotesPaymentInfoListWithFooters];
    selectedNoteStatus = '';
    uniqueNoteStatuses: string[] = [];

    constructor(
        private _errorHandler: ErrorHandler,
        private _paymentService: PaymentService,
        private _companyService: CompanyService,
        public media: MediaObserver
    ) {
        this._handleError = _errorHandler.createHandleError('PaymentNoteListComponent');
    }

    ngOnInit() {
        this.openNotesPaymentInfoListFound = true;
        this.tableDisplayStyle = this.openNotesPaymentInfoListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.openNotesPaymentInfoListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
    }

    ngAfterViewInit() { }

    getData(): void {
        this.showSpinner = true;

        this.companyInformation = this._companyService.getNewCompanyInformation();
        this.companyCode = this._companyService.getCompanyCode();

        this._companyService.companyInformation.subscribe(
            response => (this.companyInformation = response),
            error => this._handleError('companyInformation.subscribe', error)
        );

        this.getPendingPayment(this.participantId);
        this.getOpenNotesPaymentInfoList(this._participantId);
    }

    getPendingPayment(participantId: number): void {
        this.showSpinner = true;
        this._paymentService.getPendingPayment(participantId, null).subscribe(
            result => {
                this.pendingPayment = result;
                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentNoteListComponent.getPendingPayment :: result.participantId = ' + result.pendingPaymentAmt);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentNoteListComponent.getPendingPayment :: result = ' + JSON.stringify(result));
                }
                //this.getDataComplete();
            },
            error => this._handleError('getPendingPayment', error)
        );
    }

    getOpenNotesPaymentInfoList(participantId: number): void {
        this.showSpinner = true;
        this._paymentService.getOpenNotesPaymentInfoList(participantId).subscribe(
            resultArray => {
                this.openNotesPaymentInfoList = resultArray;
                this.uniqueNoteStatuses = [...new Set(this.openNotesPaymentInfoList.map(note => note.noteStatus))];
                this.openNotesPaymentInfoListWithFooters = this._paymentService.addOpenNotesPaymentInfoListFooters(resultArray);
                this.filteredNotesList = [...this.openNotesPaymentInfoListWithFooters];

                if (this._logging.serviceCallTraceLog) {
                    console.log('PaymentNoteListComponent.getOpenNotesPaymentInfoList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('PaymentNoteListComponent.getOpenNotesPaymentInfoList :: result = ' + JSON.stringify(resultArray));
                }
                this.tableDataSource = new MatTableDataSource<OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter>(
                    this.openNotesPaymentInfoListWithFooters
                );
                this.openNotesPaymentInfoListFound = resultArray.length > 0;
                this.tableDisplayStyle = this.openNotesPaymentInfoListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
                this.tableDisplayStyleIE = this.openNotesPaymentInfoListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
                this.getDataComplete();
            },
            error => this._handleError('getOpenNotesPaymentInfoList', error)
        );
    }

    filterNotes() {

        // this.selectedNoteId = null;
        // this.filteredNotesList = [...this.openNotesPaymentInfoListWithFooters];

        if (this.selectedNoteStatus) {
            this.filteredNotesList = this.openNotesPaymentInfoListWithFooters.filter(
                // note => note.noteStatus === this.selectedNoteStatus
                (note: OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter) => {
                    return 'noteStatus' in note && note.noteStatus === this.selectedNoteStatus;
                }
            );
            // const groupFooterItems = this.openNotesPaymentInfoListWithFooters.filter(
            //     (item: any) => !('noteStatus' in item) && 'paymentGroup' in item && item.paymentGroup
            // );
            // this.filteredNotesList = [...this.filteredNotesList, ...groupFooterItems];

            const noteItems = this.filteredNotesList.filter(item => 'noteStatus' in item);
            const groupFooterItems = this.openNotesPaymentInfoListWithFooters.filter(
                item => !('noteStatus' in item) && 'paymentGroup' in item && item.paymentGroup
            );

            const groupedNotes = noteItems.reduce((groups, note) => {
              const key = note.paymentGroup;
              (groups[key] = groups[key] || []).push(note);
              return groups;
            }, {} as { [key: string]: any[] });

            const sortedGroupKeys = Object.keys(groupedNotes).sort((a, b) => a.localeCompare(b));
            const sortedList: (any)[] = [];

            for (const key of sortedGroupKeys) {
              sortedList.push(...groupedNotes[key]);
              const footer = groupFooterItems.find(item => item.paymentGroup === key);
              if (footer) {
                sortedList.push(footer);
              }
            }

            this.filteredNotesList = sortedList;
        } else {
            this.filteredNotesList = [...this.openNotesPaymentInfoListWithFooters];
        }
    }

    getDataComplete(): void {
        this.showSpinner = false;
    }

    selectNote(noteId: number) {
        let paymentSelected: PaymentSelected;
        const selectedNoteId: number = noteId !== null && noteId > 0 ? noteId : null;
        paymentSelected = {
            paymentSelectType: PaymentSelectType.Note,
            selectedId: selectedNoteId,
            selectedData: this._getSelectedData(PaymentSelectType.Note, selectedNoteId, null, this.openNotesPaymentInfoList)
        };
        this.selected.emit(paymentSelected);
    }

    selectPayGroup(paymentGroup: string) {
        let paymentSelected: PaymentSelected;
        const selectedPayGroup: string = paymentGroup !== null && paymentGroup.length > 0 ? paymentGroup : null;
        paymentSelected = {
            paymentSelectType: PaymentSelectType.PayGroup,
            selectedId: selectedPayGroup,
            selectedData: this._getSelectedData(PaymentSelectType.PayGroup, null, selectedPayGroup, this.openNotesPaymentInfoList)
        };
        this.selected.emit(paymentSelected);
    }

    isGroupFooter(index, item): boolean {
        return item.isGroupFooter;
    }

    getToolTip(element: string): string {
        let toolTip: string;
        switch (element.toUpperCase().trim()) {
            case 'NOTEID': {
                toolTip = 'To obtain a copy of your contract, please contact us via the “Contact Us” link at the top of the page,' +
                    ' or you can contact your lender for a copy.';
                break;
            }
            case 'NOTESTATUS': {
                toolTip = 'What does My Loan Status Mean?';
                break;
            }
            default: {
                toolTip = 'No ToolTip message has been defined for ' + element;
                break;
            }
        }
        return toolTip;
    }

    openToolTip(element: string): void {
        switch (element.toUpperCase().trim()) {
            case 'NOTESTATUS': {
                if (this._logging.navigationTraceLog) {
                    console.log('PaymentNoteListComponent.openToolTip');
                }
                window.open(this.companyInformation.loanStatusDescriptionsURL, '_blank');
                break;
            }
        }
    }

    // 10/22/2021 - Get Company Code
    CompanyCode_Check(): boolean {
        for (const companyLookup of environment.companyCode.legaltext) {
            if (companyLookup.companyCode === this.companyCode) {
                return true;
            }
        }
        return false;
    }

    private _getSelectedData(
        paymentSelectType: PaymentSelectType,
        selectedNoteId: number,
        selectedPayGroup: string,
        openNotesPaymentInfoList: OpenNotesPaymentInfoListItem[]
    ): OpenNotesPaymentInfoListItem[] {
        let selectedData: OpenNotesPaymentInfoListItem[] = [];
        if (paymentSelectType === PaymentSelectType.Note) {
            selectedData = openNotesPaymentInfoList.filter(x => x.noteId === selectedNoteId);
        } else if (paymentSelectType === PaymentSelectType.PayGroup) {
            selectedData = openNotesPaymentInfoList.filter(x => x.paymentGroup === selectedPayGroup);
        }
        return selectedData;
    }
}
